import React, { useEffect, useState } from 'react';
import { Avatar, Flex, Progress, Text } from '@pega/cosmos-react-core';
import styled, { css, keyframes } from 'styled-components';

import useLocalTranslation from '../../../../../hooks/useLocalTranslation';

const DEFAULT_PHRASE_INTERVAL_MS = 3000;

interface AgenticTypingIndicatorProps {
  phrases?: string[];
  intervalMs?: number;
  selectedThemeColor?: string;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const StyledIndicatorContainer = styled(Flex)(
  ({ theme }) => css`
    padding: calc(0.5 * ${theme.base.spacing}) 0;
    animation: ${fadeIn} ${theme.base.animation.speed} ease-out;
  `,
);

const StyledPhaseText = styled(Text)(
  ({ theme }) => css`
    font-size: ${theme.base['font-size']};
    font-weight: ${theme.base['font-weight']['normal']};
    animation: ${fadeIn} ${theme.base.animation.speed} ease-out;
  `,
);

const StyledAvatarWrapper = styled.div<{ $avatarColor?: string }>(
  ({ theme, $avatarColor }) => css`
    min-height: 2rem;
    max-height: 2rem;
    min-width: 2rem;
    max-width: 2rem;
    align-self: flex-end;

    & > * {
      color: ${$avatarColor || theme.base.palette['brand-primary']};
      background-color: ${theme.base.palette['secondary-background']};
    }
  `,
);

const StyledProgressWrapper = styled.div<{ $progressColor?: string }>(
  ({ theme, $progressColor }) => css`
    align-items: baseline;
    margin-inline-start: calc(0.25 * ${theme.base.spacing});
    vertical-align: bottom;

    [role='progressbar'] span span {
      background: ${$progressColor || theme.base.palette['brand-primary']};
    }
  `,
);

const AgenticTypingIndicator: React.FC<AgenticTypingIndicatorProps> = ({
  phrases,
  intervalMs = DEFAULT_PHRASE_INTERVAL_MS,
  selectedThemeColor,
}) => {
  const translate = useLocalTranslation();
  const defaultPhrases = [
    translate('agentic_phase_thinking'),
    translate('agentic_phase_analyzing'),
    translate('agentic_phase_generating'),
  ];

  const filteredPhrases = (phrases || []).filter((p) => p.trim() !== '');

  const resolvedPhrases = filteredPhrases.length > 0 ? filteredPhrases : defaultPhrases;

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);

  const phraseCount = resolvedPhrases.length;
  useEffect(() => {
    if (phraseCount <= 1) return;

    const intervalId = setInterval(() => {
      setCurrentPhraseIndex((prev) => (prev + 1) % phraseCount);
    }, intervalMs);

    return () => {
      clearInterval(intervalId);
    };
  }, [phraseCount, intervalMs]);

  useEffect(() => {
    setCurrentPhraseIndex(0);
  }, [phrases]);

  const currentMessage = resolvedPhrases[currentPhraseIndex] || resolvedPhrases[0];

  return (
    <StyledIndicatorContainer
      container={{ direction: 'row', gap: 1, alignItems: 'center' }}
      role="status"
      aria-label={translate('agentic_loader_status')}
    >
      <StyledAvatarWrapper $avatarColor={selectedThemeColor}>
        <Avatar
          variant="primary"
          name=""
          icon="polaris-solid"
          shape="circle"
          size="m"
          data-testid="agentic-avatar"
          aria-hidden="true"
        />
      </StyledAvatarWrapper>

      <Flex container={{ direction: 'row', alignItems: 'center', gap: 0.25 }}>
        <StyledPhaseText key={currentPhraseIndex} aria-live="polite" aria-atomic="true">
          {currentMessage}
        </StyledPhaseText>
        <StyledProgressWrapper aria-hidden="true" $progressColor={selectedThemeColor}>
          <Progress variant="ellipsis" placement="inline" background={selectedThemeColor} />
        </StyledProgressWrapper>
      </Flex>
    </StyledIndicatorContainer>
  );
};

export default AgenticTypingIndicator;
